import { useCallback } from 'react';

import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import type { RoleOption } from 'src/components/RolesAutocomplete';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';

export function useGetRequesterUserDefinedRoles(
  options?: Parameters<typeof Apollo.useGetRequesterUserDefinedRolesQuery>[0]
) {
  const { data, loading, refetch } = Apollo.useGetRequesterUserDefinedRolesQuery(options);

  return {
    loading,
    refetch,
    userDefinedRoles: data?.userDefinedRoles ?? [],
  };
}

export function useCreateUserDefinedRole() {
  const [createRole, { loading }] = Apollo.useCreateUserDefinedRoleMutation();
  const safeCreateRole = useCallback(
    (
      options: Parameters<Apollo.CreateUserDefinedRoleMutationHookResult[0]>[0]
    ): Promise<ApolloLink.Result<Apollo.CreateUserDefinedRoleMutation>> => {
      return createRole({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              userDefinedRoles: (refs: readonly Reference[] = []) => {
                if (!data) return refs;

                const newRef = cache.writeFragment({
                  data: data.createUserDefinedRole.userDefinedRole,
                  fragment: gql`
                    fragment NewRole on UserDefinedRole {
                      __typename
                      id
                    }
                  `,
                });
                return newRef ? [...refs, newRef] : refs;
              },
            },
          });
        },
      });
    },
    [createRole]
  );

  return {
    createRole: safeCreateRole,
    loading,
  };
}

export function useMergeRoles(): {
  loading: boolean;
  mergeRoles: (
    options: Parameters<Apollo.MergeRolesMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.MergeRolesMutation>>;
} {
  const [mergeRoles, { loading }] = Apollo.useMergeRolesMutation();

  return {
    loading,
    mergeRoles: options => {
      return mergeRoles({
        ...options,
        update: (cache, { data }) => {
          // Mutation failed.  Don't update cache.
          if (!data) return;

          const rolesKey: keyof Apollo.Query = 'userDefinedRoles';
          const sourceRoleIds = new Set(options?.variables?.input.sourceRoleIds ?? []);

          cache.modify({
            fields: {
              [rolesKey]: (refs: readonly Reference[] = [], { readField }) => {
                return refs.filter(ref => {
                  const roleId = readField<UUID>('id', ref);
                  return roleId && !sourceRoleIds.has(roleId);
                });
              },
            },
          });
        },
      });
    },
  };
}

/**
 * Checks whether a user is authorized to edit roles.
 */
export function useCanModifyRoles(): {
  canModifyRoles: boolean;
} {
  const currentUser = useSelectCurrentUser();
  return { canModifyRoles: currentUser?.role === Apollo.OrgRole.Admin };
}

/** Returns a function to turn an autocomplete recipient into an Assignee. */
export function useGetRoleFromOption() {
  const { createRole, loading } = useCreateUserDefinedRole();

  return {
    getRoleFromOption: useCallback(
      async (roleOption: RoleOption | null): Promise<{ assignedRoleId: UUID | null }> => {
        if (roleOption?.__typename === 'NewRole') {
          const { data } = await createRole({
            variables: { input: { props: { roleName: roleOption.roleName } } },
          });
          return { assignedRoleId: data?.createUserDefinedRole.userDefinedRole.id ?? null };
        }

        return { assignedRoleId: roleOption?.id ?? null };
      },
      [createRole]
    ),
    loading,
  };
}
